// Admin user-management API client.
// CSRF and session cookies are injected by the global fetch patch
// (services/fetchPatch.js) for /api/* routes — no manual headers needed here.

/**
 * List user accounts (optionally filtered by a name/email query and a role).
 *
 * Paginé côté serveur : `total` / `pageCount` viennent du back, jamais de la
 * longueur du tableau reçu — celui-ci ne contient qu'une page.
 *
 * @param {Object} [options]
 * @param {string} [options.q] - search term (name or e-mail)
 * @param {string} [options.role] - global role id the accounts must carry
 * @param {number} [options.page] - 1-indexed page number
 * @param {number} [options.pageSize] - accounts per page
 * @returns {Promise<{users: Array, total: number, page: number, pageSize: number, pageCount: number}>}
 */
export async function listUsers({ q = '', role = '', page = 1, pageSize } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (role) params.set('role', role);
  params.set('page', String(page));
  if (pageSize) params.set('pageSize', String(pageSize));
  params.set('_', Date.now()); // cache-bust
  const res = await fetch(`/api/admin/users?${params}`, { credentials: 'include' });
  if (!res.ok) throw await toError(res, 'Échec du chargement des utilisateurs');
  return res.json();
}

/**
 * Fetch form metadata: assignable global roles, projects, project roles.
 * @returns {Promise<{roles: Array, projects: Array, projectRoles: Array}>}
 */
export async function fetchUserMeta() {
  const res = await fetch('/api/admin/user-meta', { credentials: 'include' });
  if (!res.ok) throw await toError(res, 'Échec du chargement des métadonnées');
  return res.json();
}

/**
 * Create a user account.
 * @param {Object} payload
 * @param {string} payload.name
 * @param {string} payload.mail
 * @param {boolean} payload.generatePassword
 * @param {string} [payload.password]
 * @param {string[]} [payload.roles]
 * @param {{projectId: number, roles: string[]}[]} [payload.memberships]
 *   Project memberships — multiple projects, multiple group roles each.
 * @returns {Promise<{user: Object, resetLink: string|null, memberships: Array}>}
 */
export async function createUser(payload) {
  const res = await fetch('/api/admin/users', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw await toError(res, "Échec de la création de l'utilisateur");
  return res.json();
}

/**
 * Update an account: name, e-mail, active status and global roles.
 *
 * `roles` carries the FULL desired set, not a diff : le serveur calcule
 * lui-même ce qu'il faut ajouter et retirer, ce qui rend l'appel rejouable et
 * évite qu'un écran resté ouvert n'écrase une modification faite ailleurs par
 * un « retire ce rôle » devenu sans objet.
 *
 * Les autres champs sont facultatifs : ceux qu'on n'envoie pas restent
 * inchangés côté serveur.
 *
 * @param {number} uid - account id
 * @param {Object} changes
 * @param {string[]} changes.roles - the complete list of global roles the account must end up with
 * @param {string} [changes.name] - new username
 * @param {string} [changes.mail] - new e-mail address
 * @param {boolean} [changes.status] - true = active, false = blocked
 * @returns {Promise<{user: Object}>}
 */
export async function updateUser(uid, changes) {
  const res = await fetch(`/api/admin/users/${uid}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!res.ok) throw await toError(res, 'Échec de la mise à jour du compte');
  return res.json();
}

/**
 * Annule un compte — le geste « Supprimer » de la liste des utilisateurs.
 *
 * « Annuler » est le mot de Drupal, et il n'est pas cosmétique : selon la
 * MÉTHODE, le compte est bloqué (ses contenus restent) ou supprimé (ses
 * contenus étant réattribués à l'anonyme ou supprimés avec lui). Les méthodes
 * proposables viennent de `fetchUserMeta().cancelMethods` — c'est le serveur
 * qui les tient, parce que les modules installés les altèrent.
 *
 * @param {number} uid - le compte visé
 * @param {Object} options
 * @param {string} options.method - id de méthode (ex. 'user_cancel_block')
 * @param {boolean} [options.notify] - prévenir l'utilisateur par e-mail
 * @param {boolean} [options.confirmMail] - ne rien faire maintenant : envoyer à
 *   l'utilisateur la demande de confirmation par e-mail
 * @returns {Promise<{status: 'deleted'|'blocked'|'confirmation_sent', user?: Object, uid?: number}>}
 */
export async function cancelUser(uid, { method, notify = false, confirmMail = false }) {
  const res = await fetch(`/api/admin/users/${uid}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method, notify, confirmMail }),
  });
  if (!res.ok) throw await toError(res, 'Échec de la suppression du compte');
  return res.json();
}

async function toError(res, fallback) {
  const body = await res.json().catch(() => ({}));
  return new Error(body?.error || fallback);
}
